import React, { useState, useEffect, useMemo } from 'react';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import TimesheetPdfDocument from './TimesheetPdfDocument';
import ExportFreeTextModal from '../shared/ExportFreeTextModal';
import { useAuth } from '../../../context/AuthContext';
import { useSafeClose } from '../../../hooks/useSafeClose';
import './Timesheet.css';

export default function TimesheetExportModal({ isOpen, onClose, entries: initialEntries, user: employee, title: initialTitle = "Stundennachweis", isAdminView = false }) {
    const { user: currentUser } = useAuth();
    const [allEntries, setAllEntries] = useState([]);

    // Config
    const [reportTitle, setReportTitle] = useState(initialTitle);
    const [employeeName, setEmployeeName] = useState(employee?.name || employee?.username || "Mitarbeiter");

    // Timeframe
    const [useTimeframe, setUseTimeframe] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [minDate, setMinDate] = useState('');
    const [maxDate, setMaxDate] = useState('');

    // Status Filter
    const [selectedStatuses, setSelectedStatuses] = useState({
        entwurf: true,
        eingereicht: true,
        genehmigt: true,
        überarbeitung_angefordert: true
    });

    // Panel States
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [selectedDetailId, setSelectedDetailId] = useState(null);

    // Free Text State
    const [freeTextHtml, setFreeTextHtml] = useState('');
    const [isFreeTextModalOpen, setIsFreeTextModalOpen] = useState(false);

    // New Export Options
    const [includeNotes, setIncludeNotes] = useState(true);
    const [includeDetails, setIncludeDetails] = useState(true);
    const [sortOrder, setSortOrder] = useState('desc');
    const [freeTextPosition, setFreeTextPosition] = useState('before_signature');

    // Initial Setup
    useEffect(() => {
        if (isOpen && initialEntries) {
            const processed = initialEntries.map(e => ({
                ...e,
                isIncluded: true
            }));
            setAllEntries(processed);

            if (processed.length > 0) {
                const dates = processed.map(e => e.entry_date).sort();
                const min = dates[0].split('T')[0];
                const max = dates[dates.length - 1].split('T')[0];
                setMinDate(min);
                setMaxDate(max);
                setStartDate(min);
                setEndDate(max);
            }

            setSelectedStatuses({
                entwurf: true,
                eingereicht: true,
                genehmigt: true,
                überarbeitung_angefordert: true
            });

            setReportTitle(initialTitle);
            setEmployeeName(employee?.name || employee?.username || "Mitarbeiter");
            setIsDetailsOpen(false);
            setFreeTextHtml('');
            setFreeTextPosition('before_signature');
            setIsFreeTextModalOpen(false);
        }
    }, [isOpen, initialEntries, employee, isAdminView, initialTitle]);

    const displayEntries = useMemo(() => {
        let result = allEntries.filter(e => {
            // Status Filter
            const statusKey = e.status || 'entwurf';
            if (!selectedStatuses[statusKey]) return false;

            // Timeframe Filter
            if (useTimeframe) {
                const d = e.entry_date.split('T')[0];
                if (startDate && d < startDate) return false;
                if (endDate && d > endDate) return false;
            }

            return true;
        });

        // Sort by date
        result.sort((a, b) => {
            const dateA = new Date(a.entry_date);
            const dateB = new Date(b.entry_date);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
        return result;
    }, [allEntries, selectedStatuses, useTimeframe, startDate, endDate, sortOrder]);

    const finalEntries = useMemo(() => {
        return displayEntries.filter(e => e.isIncluded);
    }, [displayEntries]);

    const handleToggleEntry = (id) => {
        setAllEntries(prev => prev.map(e => e.id === id ? { ...e, isIncluded: !e.isIncluded } : e));
    };

    const handleSelectAll = (select) => {
        const ids = displayEntries.map(e => e.id);
        setAllEntries(prev => prev.map(e => ids.includes(e.id) ? { ...e, isIncluded: select } : e));
    };

    const getFilterText = () => {
        let parts = [];
        if (useTimeframe) {
            parts.push(`${new Date(startDate).toLocaleDateString('de-DE')} - ${new Date(endDate).toLocaleDateString('de-DE')}`);
        } else {
            parts.push("Gesamter Zeitraum");
        }

        const activeStatuses = Object.keys(selectedStatuses).filter(k => selectedStatuses[k]);
        if (activeStatuses.length < 4) {
            parts.push(`Status: ${activeStatuses.length === 0 ? 'Keine' : activeStatuses.join(', ')}`);
        }

        return parts.join(' | ');
    };

    const handleResetFilters = () => {
        setUseTimeframe(false);
        setSortOrder('desc');
        setSelectedStatuses({
            entwurf: true,
            eingereicht: true,
            genehmigt: true,
            überarbeitung_angefordert: true
        });
        if (initialEntries.length > 0) {
            const dates = initialEntries.map(e => e.entry_date).sort();
            setStartDate(dates[0].split('T')[0]);
            setEndDate(dates[dates.length - 1].split('T')[0]);
        }
    };

    const hasActiveFilters = useTimeframe ||
        sortOrder !== 'desc' ||
        Object.values(selectedStatuses).some(v => !v);

    const pdfFilename = `${new Date().toISOString().split('T')[0]}_Stundennachweis_${employeeName.replace(/\s+/g, '_')}.pdf`;

    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp} style={{ zIndex: 9999 }}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '95vw', height: '95vh', maxWidth: '1400px', display: 'flex', flexDirection: 'column', padding: '15px 25px' }}>
                <div className="modal-header" style={{ marginBottom: '15px' }}>
                    <h2>PDF Export: {employeeName}</h2>
                    <button onClick={onClose} className="btn-close-text">&times;</button>
                </div>

                <div style={{ display: 'flex', flex: 1, overflow: 'hidden', gap: '20px' }}>
                    {/* Left - Config */}
                    <div style={{ width: '260px', minWidth: '260px', backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', overflowY: 'auto', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Konfiguration</h3>
                            {hasActiveFilters && (
                                <button onClick={handleResetFilters} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}>
                                    Zurücksetzen
                                </button>
                            )}
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Titel</label>
                            <input type="text" value={reportTitle} onChange={e => setReportTitle(e.target.value)} />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <button
                                onClick={() => setIsFreeTextModalOpen(true)}
                                style={{ width: '100%', padding: '8px 12px', background: '#fff', border: '1px solid #3b82f6', color: '#3b82f6', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 'bold' }}
                            >
                                <span>📝</span> Freitext hinzufügen {freeTextHtml && '✓'}
                            </button>

                            {freeTextHtml && (
                                <div className="form-group" style={{ marginTop: '15px' }}>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', fontSize: '0.85rem' }}>Position Freitext</label>
                                    <select
                                        value={freeTextPosition}
                                        onChange={e => setFreeTextPosition(e.target.value)}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
                                    >
                                        <option value="between_cover_entries">Nach dem Deckblatt</option>
                                        <option value="before_total">Vor der Gesamtsumme</option>
                                        <option value="before_signature">Vor den Unterschriften</option>
                                        <option value="after_signature">Am Ende</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Zeitraum</label>
                            <select
                                value={useTimeframe ? 'custom' : 'all'}
                                onChange={e => setUseTimeframe(e.target.value === 'custom')}
                                style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                            >
                                <option value="all">Alle Einträge</option>
                                <option value="custom">Zeitraum wählen</option>
                            </select>

                            {useTimeframe && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <input type="date" value={startDate} min={minDate} max={maxDate} onChange={e => setStartDate(e.target.value)} />
                                    <input type="date" value={endDate} min={minDate} max={maxDate} onChange={e => setEndDate(e.target.value)} />
                                </div>
                            )}
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Status Filter</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {!isAdminView && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={selectedStatuses.entwurf} onChange={e => setSelectedStatuses(prev => ({ ...prev, entwurf: e.target.checked }))} />
                                        <span>Entwurf</span>
                                    </label>
                                )}
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedStatuses.eingereicht} onChange={e => setSelectedStatuses(prev => ({ ...prev, eingereicht: e.target.checked }))} />
                                    <span>Eingereicht</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedStatuses.überarbeitung_angefordert} onChange={e => setSelectedStatuses(prev => ({ ...prev, überarbeitung_angefordert: e.target.checked }))} />
                                    <span>Korrektur angefordert</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedStatuses.genehmigt} onChange={e => setSelectedStatuses(prev => ({ ...prev, genehmigt: e.target.checked }))} />
                                    <span>Genehmigt</span>
                                </label>
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Optionen</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={includeNotes} onChange={e => setIncludeNotes(e.target.checked)} />
                                    <span>Notizen mit exportieren</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={includeDetails} onChange={e => setIncludeDetails(e.target.checked)} />
                                    <span>Details exportieren</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Sortierung</label>
                            <select
                                value={sortOrder}
                                onChange={e => setSortOrder(e.target.value)}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                            >
                                <option value="desc">Neu → Alt</option>
                                <option value="asc">Alt → Neu</option>
                            </select>
                        </div>

                        <div style={{ marginTop: 'auto', borderTop: '1px solid #cbd5e1', paddingTop: '20px' }}>
                            <PDFDownloadLink
                                document={<TimesheetPdfDocument entries={finalEntries} reportTitle={reportTitle} employeeName={employeeName} filterText={getFilterText()} creatorName={currentUser?.name || currentUser?.username} includeNotes={includeNotes} includeDetails={includeDetails} freeTextHtml={freeTextHtml} freeTextPosition={freeTextPosition} />}
                                fileName={pdfFilename}
                                style={{ textDecoration: 'none' }}
                            >
                                {({ blob, url, loading, error }) => (
                                    <button className="btn-primary" style={{ width: '100%', background: '#10b981', borderColor: '#059669', padding: '12px', borderRadius: '6px', cursor: 'pointer' }} disabled={loading || finalEntries.length === 0}>
                                        {loading ? 'Generiere PDF...' : `PDF Herunterladen (${finalEntries.length})`}
                                    </button>
                                )}
                            </PDFDownloadLink>
                        </div>
                    </div>

                    {/* Middle - Detail Adjustment (Collapsed by default) */}
                    <div style={{ display: 'flex', height: '100%' }}>
                        {!isDetailsOpen ? (
                            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                <button
                                    onClick={() => setIsDetailsOpen(true)}
                                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px 5px', height: '100%', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#64748b', transition: 'all 0.2s', minWidth: '40px' }}
                                    title="Details anpassen aufklappen"
                                >
                                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>▶</span>
                                    <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 'bold', letterSpacing: '1px' }}>Details anpassen</span>
                                </button>
                            </div>
                        ) : (
                            <div style={{ width: '320px', minWidth: '320px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '-2px 0 10px rgba(0,0,0,0.05)' }}>
                                <div style={{ padding: '12px 15px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 'bold', color: '#1e293b', whiteSpace: 'nowrap' }}>Details anpassen</h3>
                                    <button
                                        onClick={() => setIsDetailsOpen(false)}
                                        style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', width: '22px', height: '22px', padding: 0, minWidth: '22px', flexShrink: 0 }}
                                        title="Zuklappen"
                                    >
                                        ◀
                                    </button>
                                </div>
                                <div style={{ padding: '10px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '10px' }}>
                                    <button onClick={() => handleSelectAll(true)} style={{ flex: 1, padding: '5px', fontSize: '0.8rem' }}>Alle wählen</button>
                                    <button onClick={() => handleSelectAll(false)} style={{ flex: 1, padding: '5px', fontSize: '0.8rem' }}>Keine wählen</button>
                                </div>
                                <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
                                    {displayEntries.length === 0 ? <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>Keine Einträge für diese Filter.</p> : (
                                        displayEntries.map(entry => {
                                            const isSelected = selectedDetailId === entry.id;
                                            let tasks = [];
                                            try { tasks = typeof entry.tasks === 'string' ? JSON.parse(entry.tasks) : (entry.tasks || []); } catch (e) { }
                                            if (!tasks.length) tasks = [{ description: entry.activity, hours: entry.hours }];

                                            return (
                                                <div key={entry.id} style={{ marginBottom: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                                                    <div
                                                        style={{ padding: '10px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', backgroundColor: isSelected ? '#eff6ff' : '#fff' }}
                                                        onClick={() => setSelectedDetailId(isSelected ? null : entry.id)}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={entry.isIncluded}
                                                            onChange={(e) => { e.stopPropagation(); handleToggleEntry(entry.id); }}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <div style={{ flex: 1, fontSize: '0.9rem' }}>
                                                            <strong>{new Date(entry.entry_date).toLocaleDateString('de-DE')}</strong>
                                                        </div>
                                                        <div style={{ fontSize: '0.9rem', color: '#3b82f6', fontWeight: 'bold' }}>
                                                            {parseFloat(entry.hours).toFixed(2)} h
                                                        </div>
                                                        <span>{isSelected ? '▼' : '▶'}</span>
                                                    </div>

                                                    {isSelected && (
                                                        <div style={{ padding: '10px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f9fafb', fontSize: '0.85rem' }}>
                                                            {tasks.map((t, idx) => (
                                                                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                                    <span style={{ color: '#475569' }}>• {t.description}</span>
                                                                    <span style={{ fontWeight: 'bold' }}>{parseFloat(t.hours).toFixed(2)} h</span>
                                                                </div>
                                                            ))}
                                                            {entry.notes && (
                                                                <div style={{ marginTop: '8px', borderTop: '1px dashed #cbd5e1', paddingTop: '4px', fontStyle: 'italic', color: '#64748b' }}>
                                                                    Notiz: {entry.notes}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right - Preview */}
                    <div style={{ flex: 1, backgroundColor: '#cbd5e1', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {finalEntries.length === 0 ? (
                            <div style={{ margin: 'auto', textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '12px' }}>
                                <h3 style={{ color: '#64748b' }}>Keine Einträge für PDF ausgewählt</h3>
                                <p style={{ color: '#94a3b8' }}>Wählen Sie mindestens einen Tag für den Export.</p>
                            </div>
                        ) : (
                            <PDFViewer key={`pdf-${finalEntries.length}-${finalEntries.map(e => e.id).join('')}`} width="100%" height="100%" showToolbar={false} style={{ border: 'none' }}>
                                <TimesheetPdfDocument
                                    entries={finalEntries}
                                    reportTitle={reportTitle}
                                    employeeName={employeeName}
                                    filterText={getFilterText()}
                                    creatorName={currentUser?.name || currentUser?.username}
                                    includeNotes={includeNotes}
                                    includeDetails={includeDetails}
                                    freeTextHtml={freeTextHtml}
                                    freeTextPosition={freeTextPosition}
                                />
                            </PDFViewer>
                        )}
                    </div>
                </div>
            </div>

            {/* Free Text Modal */}
            <ExportFreeTextModal
                isOpen={isFreeTextModalOpen}
                onClose={() => setIsFreeTextModalOpen(false)}
                initialHtml={freeTextHtml}
                onSave={setFreeTextHtml}
            />
        </div>
    );
}
